import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { ClerkProvider, useAuth } from "@clerk/react";
import { ConvexReactClient } from "convex/react";
import { ConvexProviderWithClerk } from "convex/react-clerk";
import { Toaster } from "@/components/ui/sonner";
import { RouterProvider } from "@tanstack/react-router";
import { router } from "./router";
import { ConfigError } from "./components/states";
import "./index.css";

/**
 * ClerkProvider must wrap ConvexProviderWithClerk, never the other way round —
 * Convex needs to read Clerk's context. See docs/SETUP.md.
 */

const convexUrl = import.meta.env.VITE_CONVEX_URL;
const clerkKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

const root = createRoot(document.getElementById("root")!);

// A missing env var must say so on the screen. A blank white page two minutes
// before a demo is the worst possible failure mode, and this is the one class
// of error that survives a successful build.
if (!convexUrl || !clerkKey) {
  root.render(
    <StrictMode>
      <ConfigError
        missing={[
          !convexUrl && "VITE_CONVEX_URL",
          !clerkKey && "VITE_CLERK_PUBLISHABLE_KEY",
        ].filter(Boolean) as string[]}
      />
    </StrictMode>,
  );
} else {
  const convex = new ConvexReactClient(convexUrl);

  root.render(
    <StrictMode>
      <ClerkProvider publishableKey={clerkKey} afterSignOutUrl="/">
        <ConvexProviderWithClerk client={convex} useAuth={useAuth}>
          <RouterProvider router={router} />
          <Toaster position="top-center" richColors closeButton />
        </ConvexProviderWithClerk>
      </ClerkProvider>
    </StrictMode>,
  );
}
